import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, MapPin, Sparkles, LayoutDashboard, Calendar, Wallet, BookOpen, Plane, HelpCircle, Headphones } from 'lucide-react';
import { Button } from './ui/button';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Triple A Travel Solutions!',
    description: 'Let us give you a quick tour of the key features to help you plan your perfect trip.',
    icon: Sparkles,
    target: null,
    route: null,
    position: 'center',
  },
  {
    id: 'plan',
    title: 'Plan Your Trip',
    description: 'Use the "Plan" menu to start a New Trip, browse Templates, calculate your budget, compare destinations, generate packing lists, and check visa requirements — all in one place.',
    icon: MapPin,
    target: '[data-testid="header-plan-dropdown"]',
    route: null,
    position: 'bottom',
  },
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    description: 'Access all your planned trips from the Dashboard. View, edit, or delete any itinerary. You can also find your Journal, Stories, Blogs, and Budget Tracker here.',
    icon: LayoutDashboard,
    target: '[data-testid="header-dashboard-dropdown"]',
    route: null,
    position: 'bottom',
  },
  {
    id: 'schedule',
    title: 'AI-Generated Itinerary',
    description: 'Each trip gets a detailed day-by-day schedule with morning, afternoon, and evening activities. Explore nearby alternatives for each stop, view photos, and get Google Maps directions.',
    icon: Calendar,
    target: null,
    route: null,
    position: 'center',
    image: 'schedule',
  },
  {
    id: 'budget',
    title: 'Track Your Budget',
    description: 'Use the Budget Tracker to log expenses by category (food, transport, accommodation, activities). View spending breakdowns, daily averages, and export budget reports as PDF.',
    icon: Wallet,
    target: null,
    route: null,
    position: 'center',
    image: 'budget',
  },
  {
    id: 'stories-blog',
    title: 'Share Your Journey',
    description: 'Create Trip Stories with photos and notes from your travels. Generate AI-powered Travel Blogs with cover images. Share everything via WhatsApp, Twitter, Facebook, or email!',
    icon: BookOpen,
    target: null,
    route: null,
    position: 'center',
    image: 'share',
  },
  {
    id: 'booking',
    title: 'Book Travel Services',
    description: 'Use the "Book" menu to search flights, hotels, and tour packages. You can also book airport transfers, local trips, car rentals, activities, travel insurance, eSIMs, flight compensation, and luggage storage — all from trusted providers.',
    icon: Plane,
    target: '[data-testid="header-book-dropdown"]',
    route: null,
    position: 'bottom',
  },
  {
    id: 'assisted',
    title: 'Need Personal Help?',
    description: 'Visit "Assisted Services" for personalised support. Our travel experts can help with flight bookings, hotel reservations, visa assistance, custom tour packages, insurance guidance, and group or corporate travel — just call, email, or WhatsApp us.',
    icon: Headphones,
    target: '[data-testid="header-assisted-services-btn"]',
    route: null,
    position: 'bottom',
  },
  {
    id: 'finish',
    title: "You're All Set!",
    description: "Start by creating your first trip — go to Plan > New Trip. Need help anytime? Click the help button or visit our FAQ page. Happy travels!",
    icon: Sparkles,
    target: null,
    route: null,
    position: 'center',
  },
];

/* ─── Feature preview cards for center-positioned steps ─── */
const FeaturePreview = ({ type }) => {
  const previews = {
    schedule: [
      { label: 'Morning', desc: 'Visit Senso-ji Temple', color: 'bg-amber-100 text-amber-700' },
      { label: 'Afternoon', desc: 'Explore Shibuya Crossing', color: 'bg-sky-100 text-sky-700' },
      { label: 'Evening', desc: 'Dinner in Shinjuku', color: 'bg-indigo-100 text-indigo-700' },
    ],
    budget: [
      { label: 'Food', amount: '$45', pct: 35, color: 'bg-orange-500' },
      { label: 'Transport', amount: '$28', pct: 22, color: 'bg-blue-500' },
      { label: 'Activities', amount: '$55', pct: 43, color: 'bg-teal-500' },
    ],
    share: [
      { label: 'Trip Story', desc: 'Photos + timeline notes', color: 'bg-amber-100 text-amber-700' },
      { label: 'AI Blog', desc: 'Auto-generated article', color: 'bg-sky-100 text-sky-700' },
      { label: 'Social Share', desc: 'WhatsApp, Twitter, Email', color: 'bg-green-100 text-green-700' },
    ],
  };

  const items = previews[type];
  if (!items) return null;

  if (type === 'budget') {
    return (
      <div className="mt-4 space-y-2">
        {items.map((item, i) => (
          <div key={item.label || `stat-${i}`} className="flex items-center gap-3">
            <span className="text-xs text-stone-600 w-16">{item.label}</span>
            <div className="flex-1 bg-stone-100 rounded-full h-2">
              <div className={`${item.color} h-2 rounded-full transition-all duration-700`} style={{ width: `${item.pct}%` }} />
            </div>
            <span className="text-xs font-semibold text-stone-700 w-10 text-right">{item.amount}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-4 grid grid-cols-3 gap-2">
      {items.map((item, i) => (
        <div key={item.label || `item-${i}`} className={`${item.color} rounded-xl p-2.5 text-center`}>
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{item.label}</p>
          <p className="text-xs mt-0.5 font-medium">{item.desc}</p>
        </div>
      ))}
    </div>
  );
};

/* ─── Spotlight overlay for targeted steps ─── */
const Spotlight = ({ rect }) => {
  if (!rect) return null;
  const pad = 8;
  return (
    <svg className="fixed inset-0 z-[10000] pointer-events-none" width="100%" height="100%">
      <defs>
        <mask id="tour-mask">
          <rect x="0" y="0" width="100%" height="100%" fill="white" />
          <rect
            x={rect.left - pad} y={rect.top - pad}
            width={rect.width + pad * 2} height={rect.height + pad * 2}
            rx="12" fill="black"
          />
        </mask>
      </defs>
      <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.65)" mask="url(#tour-mask)" />
    </svg>
  );
};

/* ─── Main Tour Component ─── */
export const GuidedTour = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [visible, setVisible] = useState(true);
  const tooltipRef = useRef(null);

  const current = TOUR_STEPS[step];
  const Icon = current.icon;
  const isFirst = step === 0;
  const isLast = step === TOUR_STEPS.length - 1;
  const hasTarget = !!current.target;

  const updateTargetRect = useCallback(() => {
    if (!current.target) { setTargetRect(null); return; }
    const el = document.querySelector(current.target);
    if (el) {
      const r = el.getBoundingClientRect();
      setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setTargetRect(null);
    }
  }, [current.target]);

  useEffect(() => {
    updateTargetRect();
    window.addEventListener('resize', updateTargetRect);
    window.addEventListener('scroll', updateTargetRect, true);
    return () => {
      window.removeEventListener('resize', updateTargetRect);
      window.removeEventListener('scroll', updateTargetRect, true);
    };
  }, [step, updateTargetRect]);

  const handleNext = () => {
    if (isLast) { handleClose(); return; }
    setStep(s => s + 1);
  };

  const handlePrev = () => {
    if (!isFirst) setStep(s => s - 1);
  };

  const handleClose = () => {
    setVisible(false);
    localStorage.setItem('triplea_tour_completed', 'true');
    onComplete?.();
  };

  if (!visible) return null;

  // Tooltip position
  let tooltipStyle = {};
  if (hasTarget && targetRect) {
    const tooltipWidth = 380;
    let left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
    left = Math.max(16, Math.min(left, window.innerWidth - tooltipWidth - 16));
    tooltipStyle = {
      position: 'fixed',
      top: targetRect.top + targetRect.height + 16,
      left,
      width: tooltipWidth,
    };
  }

  return (
    <>
      {/* Backdrop */}
      {hasTarget && targetRect ? (
        <Spotlight rect={targetRect} />
      ) : (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10000] bg-black/65 backdrop-blur-sm"
        />
      )}

      {/* Tooltip / Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          ref={tooltipRef}
          initial={{ opacity: 0, y: 10, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.97 }}
          transition={{ duration: 0.25 }}
          className={`z-[10001] ${hasTarget && targetRect ? 'fixed' : 'fixed inset-0 flex items-center justify-center p-4'}`}
          style={hasTarget && targetRect ? tooltipStyle : {}}
          data-testid="guided-tour-tooltip"
        >
          <div className={`bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden ${hasTarget && targetRect ? '' : 'max-w-md w-full'}`}>
            {/* Progress bar */}
            <div className="h-1 bg-stone-100">
              <div
                className="h-1 bg-gradient-to-r from-amber-500 to-teal-500 transition-all duration-500"
                style={{ width: `${((step + 1) / TOUR_STEPS.length) * 100}%` }}
              />
            </div>

            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-teal-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-teal-700" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-stone-900">{current.title}</h3>
                    <p className="text-[10px] text-stone-400 font-medium">Step {step + 1} of {TOUR_STEPS.length}</p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="w-7 h-7 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-stone-400 hover:text-stone-600 transition-colors flex-shrink-0"
                  data-testid="tour-skip-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Description */}
              <p className="text-sm text-stone-600 leading-relaxed">{current.description}</p>

              {/* Feature preview */}
              {current.image && <FeaturePreview type={current.image} />}

              {/* Actions */}
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-stone-100">
                <div className="flex items-center gap-1.5">
                  {TOUR_STEPS.map((s, i) => (
                    <div
                      key={`step-dot-${s.target || i}`}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${i === step ? 'bg-teal-600 w-5' : i < step ? 'bg-teal-300' : 'bg-stone-200'}`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  {!isFirst && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrev}
                      className="rounded-full h-9 px-4 text-xs"
                      data-testid="tour-prev-btn"
                    >
                      <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
                    </Button>
                  )}
                  {isFirst && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClose}
                      className="rounded-full h-9 px-4 text-xs text-stone-500"
                      data-testid="tour-skip-all-btn"
                    >
                      Skip Tour
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={handleNext}
                    className="bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-full h-9 px-5 text-xs font-semibold hover:shadow-lg transition-all"
                    data-testid="tour-next-btn"
                  >
                    {isLast ? "Let's Go!" : 'Next'} {!isLast && <ChevronRight className="w-3.5 h-3.5 ml-1" />}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

/* ─── Help Button (floating) ─── */
export const TourHelpButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="fixed bottom-6 right-6 z-[9990] w-12 h-12 rounded-full bg-gradient-to-br from-teal-600 to-teal-700 text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-200 flex items-center justify-center"
    data-testid="tour-help-btn"
    title="Take a tour"
  >
    <HelpCircle className="w-5 h-5" />
  </button>
);
